import ReturnHeaderMeta from "@dana/Components/StandardReturns/ReturnHeaderMeta";
import PlusIcon from "@dana/Icons/PlusIcon";
import { themeBackgrounds, themeColors } from "@dana/MobiusOverrides/styles/colors";
import { fontSizes, fontWeights, lineHeights } from "@dana/MobiusOverrides/styles/typography";
import ApplicationStateDana from "@dana/Store/DanaApplicationState";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { IconWrapper } from "@insite/mobius/Icon";
import Modal from "@insite/mobius/Modal";
import Tag, { TagPresentationProps } from "@insite/mobius/Tag";
import TextField from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

// --- Props Interface ---
interface StepEmailContactsProps {
    emails?: string[];
    addEmails: (newOnes: string[]) => void;
    removeEmail: (index: number) => void;
}

// --- Styles Interfaces and Objects ---
export interface StepEmailContactsStyles {
    container?: GridContainerProps;
    headingContainer?: GridContainerProps;
    titleGridItem?: GridItemProps;
    titleText?: TypographyPresentationProps;
    addRecipientWrapper?: InjectableCss;
    recepientEmails?: InjectableCss;
    addButton?: ButtonPresentationProps;
    modalButtons?: InjectableCss;
    addRecepientEmail?: ButtonPresentationProps;
    cancleAdd?: ButtonPresentationProps;
    modalMessage?: InjectableCss;
    emailInputContainer?: InjectableCss;
    modalHeadingText?: TypographyPresentationProps;
    emailTag?: TagPresentationProps;
}

export const StepEmailContactsStyles: StepEmailContactsStyles = {
    container: {
        gap: 0,
        css: css`
            background: ${themeBackgrounds.white};
        `,
    },
    headingContainer: {
        gap: 0,
        css: css`
            background: ${themeBackgrounds.lightGray};
            padding: 16px;
        `,
    },
    titleGridItem: {
        width: 10,
        css: css`
            align-items: center;
        `,
    },
    titleText: {
        variant: "h3",
        css: css`
            margin-bottom: 16px;
        `,
    },
    addRecipientWrapper: {
        css: css`
            width: 100%;
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            padding: 32px 0;
        `,
    },
    recepientEmails: {
        css: css`
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            gap: 8px;
            margin-right: 16px;
        `,
    },
    addButton: {
        variant: "tertiary",
        icon: {
            src: PlusIcon,
            position: "left",
        },
        css: css`
            ${IconWrapper} {
                svg {
                    path {
                        fill: ${themeColors.primary.danaBlue};
                    }
                }
            }

            &:hover {
                ${IconWrapper} {
                    svg {
                        path {
                            fill: ${themeColors.secondary.danaDarkBlue};
                        }
                    }
                }
            }
        `,
    },
    modalButtons: {
        css: css`
            margin-top: 16px;
            display: flex;
            flex-direction: row;
            gap: 16px;
        `,
    },
    cancleAdd: {
        variant: "secondary",
    },
    modalMessage: {
        css: css`
            margin-bottom: 16px;
            color: ${themeColors.danger};
        `,
    },
    emailInputContainer: {
        css: css`
            display: flex;
            align-items: flex-end;
            gap: 10px;
            margin-bottom: 8px;
        `,
    },
    modalHeadingText: {
        variant: "h6",
        weight: fontWeights.semiBold,
        css: css`
            margin-bottom: 10px;
        `,
    },
    emailTag: {
        css: css`
            max-width: unset;
        `,
    },
};

const styles = StepEmailContactsStyles;

const MAX_RECIPIENTS = 5;

const isValidEmail = (s: string) => /\S+@\S+\.\S+/.test(s);
const normalize = (e?: string) => (e ?? "").trim().toLowerCase();

const mapStateToProps = (state: ApplicationStateDana) => {
    return {
        standardReturnSate: state.standardReturnState,
        userEmail: state?.context?.session?.email,
    };
};
type Props = ReturnType<typeof mapStateToProps> & StepEmailContactsProps;

// --- Component Definition ---
const StepEmailContacts: FC<Props> = ({ standardReturnSate, addEmails, removeEmail, userEmail }) => {
    const [openModal, setOpenModal] = useState(false);
    const [modalMessage, setModalMessage] = useState("");
    const [emailInputs, setEmailInputs] = useState<string[]>(Array(MAX_RECIPIENTS).fill(""));
    const [emailErrors, setEmailErrors] = useState<string[]>(Array(MAX_RECIPIENTS).fill(""));
    const [recipients, setRecipients] = useState<string[]>([]);
    const [emails, setEmails] = useState<string[]>(standardReturnSate?.emailContacts);
    const [includeUserEmail, setIncludeUserEmail] = useState(true);

    const normalizedUser = useMemo(() => normalize(userEmail), [userEmail]);
    const showUserTag = !!normalizedUser && isValidEmail(normalizedUser) && includeUserEmail;

    useEffect(() => {
        setEmails(standardReturnSate?.emailContacts);
    }, [standardReturnSate?.emailContacts]);

    const validateEmail = (email: string): boolean => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handleOpenModal = () => {
        if (emails.length >= MAX_RECIPIENTS) {
            setModalMessage(siteMessage("OnePageCheckout_AdditionalEmail_Message") as string);
        } else {
            setModalMessage("");
        }
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setModalMessage("");
        setEmailInputs(Array(MAX_RECIPIENTS).fill(""));
        setEmailErrors(Array(MAX_RECIPIENTS).fill(""));
    };

    const handleAddEmails = () => {
        let hasError = false;
        const newEmailErrors = Array(MAX_RECIPIENTS).fill("");
        const emailsToAdd: string[] = [];
        const currentModalEmails = new Set<string>();

        const normalizedUser = normalize(userEmail);

        emailInputs.forEach((email, index) => {
            const trimmedEmail = email.trim();

            if (!trimmedEmail) {
                return;
            }

            if (!validateEmail(trimmedEmail)) {
                newEmailErrors[index] = translate("Invalid email format.");
                hasError = true;
                return;
            }

            if (normalizedUser && email === normalizedUser && includeUserEmail) {
                newEmailErrors[index] = translate("This email is your account email and is already included.");
                hasError = true;
                return;
            }

            if (emails.some(e => normalize(e) === email)) {
                newEmailErrors[index] = translate("This email is already added.");
                hasError = true;
                return;
            }

            if (currentModalEmails.has(trimmedEmail)) {
                newEmailErrors[index] = translate("This email is a duplicate in the current list.");
                hasError = true;
                return;
            }

            emailsToAdd.push(trimmedEmail);
            currentModalEmails.add(trimmedEmail);
        });

        setEmailErrors(newEmailErrors);

        if (hasError) {
            setModalMessage(translate("Please correct the errors in the email fields."));
            return;
        }

        const validNewEmails = emailsToAdd.filter(email => !emails.includes(email));

        if (validNewEmails.length === 0) {
            setModalMessage(translate("No new valid emails to add."));
            return;
        }

        if (emails.length + validNewEmails.length > MAX_RECIPIENTS) {
            setModalMessage(siteMessage("OnePageCheckout_AdditionalEmail_Message") as string);
            return;
        }

        addEmails(validNewEmails);
        handleCloseModal();
    };

    const handleEmailInputChange = (value: string, index: number) => {
        const newEmailInputs = [...emailInputs];
        newEmailInputs[index] = value;
        setEmailInputs(newEmailInputs);

        const newEmailErrors = [...emailErrors];
        newEmailErrors[index] = "";
        setEmailErrors(newEmailErrors);
        setModalMessage("");
    };

    const availableInputCount = Math.max(0, MAX_RECIPIENTS - emails.length);

    return (
        <StyledWrapper>
            <ReturnHeaderMeta />
            <Typography variant="h3" {...styles.titleText}>
                {translate("Email Confirmation Contact")}
            </Typography>
            <Typography as="p">
                {translate(
                    "Enter an email address below to receive a standard return confirmation email. This will include your RMA form and shipping instructions",
                )}
            </Typography>
            <StyledWrapper {...styles.addRecipientWrapper}>
                <StyledWrapper {...styles.recepientEmails}>
                    {showUserTag && (
                        <Tag
                            key="user-email"
                            onDelete={() => setIncludeUserEmail(false)}
                            data-test-selector="recipientChip-user"
                        >
                            {normalizedUser}
                        </Tag>
                    )}

                    {emails.map((email, index) => (
                        <Tag
                            {...styles.emailTag}
                            key={email}
                            onDelete={() => removeEmail(index)}
                            data-test-selector={`recipientChip-${index}`}
                        >
                            {email}
                        </Tag>
                    ))}
                </StyledWrapper>

                <Button
                    onClick={handleOpenModal}
                    data-test-selector="addRecipientsButton"
                    {...styles.addButton}
                    disabled={emails.length >= MAX_RECIPIENTS}
                >
                    {translate("Add Recipient(s)")}
                </Button>
            </StyledWrapper>

            <Modal
                isOpen={openModal}
                headline={translate("Add Recipients")}
                handleClose={handleCloseModal}
                aria-labelledby="add-recipient-modal-title"
                aria-describedby="add-recipient-modal-description"
            >
                <StyledWrapper>
                    {modalMessage && (
                        <StyledWrapper data-test-selector="modalLimitMessage" {...styles.modalMessage}>
                            {modalMessage}
                        </StyledWrapper>
                    )}

                    {availableInputCount > 0 && (
                        <Typography as="h4" {...styles.modalHeadingText}>
                            {translate("Email Address")}
                        </Typography>
                    )}

                    {Array.from({ length: availableInputCount }).map((_, index) => (
                        <StyledWrapper key={index} {...styles.emailInputContainer}>
                            <TextField
                                name={`email-input-${index}`}                                       
                                id={`email-input-${index}`}
                                autoComplete={`section-recipient-${index} email`}
                                value={emailInputs[index] || ""}
                                onChange={e => handleEmailInputChange(e.target.value, index)}
                                data-test-selector={`recipientEmail-${index}`}
                                error={emailErrors[index]}
                            />
                        </StyledWrapper>
                    ))}

                    <StyledWrapper {...styles.modalButtons}>
                        <Button
                            onClick={handleAddEmails}
                            data-test-selector="addEmailButton"
                            disabled={emails.length >= MAX_RECIPIENTS || availableInputCount === 0}
                            {...styles.addRecepientEmail}
                        >
                            {translate("Add")}
                        </Button>
                        <Button onClick={handleCloseModal} data-test-selector="closeModalButton" {...styles.cancleAdd}>
                            {translate("Cancel")}
                        </Button>
                    </StyledWrapper>
                </StyledWrapper>
            </Modal>
        </StyledWrapper>
    );
};
export default connect(mapStateToProps)(StepEmailContacts);
